import React, { useState, useEffect, useMemo } from "react";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../../services/apiService";

interface RefundRequest {
  id: number;
  event_id: number;
  purchase_user_id: number;
  host_user_id: number;
  status: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  purchase_ticket_id: number;
  reason_for_request: string;
  event_name: string;
  host_name: string;
  purchase_user_name: string;
  amount: string | number;
  host_revenue: string | number;
}

export default function RefundApprovedRequests() {
  const [refunds, setRefunds] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get("eventId");

  const [eventId, setEventId] = useState<any>(queryEventId);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // ✅ Extract eventId from query params
  const columns: any = [
    { key: "id", label: "ID" },
    { key: "event_name", label: "Event Name" },
    {
      key: "amount",
      label: "Amount",
      render: (val: any) => `$${val}`,
    },
    {
      key: "event_refund_status",
      label: "Event Refund Status",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            val === "completed"
              ? "bg-green-100 text-green-800"
              : val === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {val || "-"}
        </span>
      ),
    },
    {
      key: "host_refunded_status",
      label: "Host Refund Status",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            val === "completed"
              ? "bg-green-100 text-green-800"
              : val === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {val || "-"}
        </span>
      ),
    },
    { key: "refund_id", label: "Refund ID" },
    { key: "user_id", label: "User ID" },
    {
      key: "host_user_id",
      label: "Host ID",
    },
    {
      key: "host_first_name",
      label: "Host Name",
      render: (_: any, row: any) =>
        `${row.host_first_name || ""} ${row.host_last_name || ""}`.trim() ||
        "-",
    },
    {
      key: "refund_mail_send_count",
      label: "Mail Sent Count",
    },
    {
      key: "created_at",
      label: "Created At",
      render: (val: string) => (val ? new Date(val).toLocaleString() : "N/A"),
    },
    {
      key: "updated_at",
      label: "Updated At",
      render: (val: string) => (val ? new Date(val).toLocaleString() : "N/A"),
    },
    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded space-x-2 bg-white/30 backdrop-blur-md shadow-md",
      render: (_: any, row: any) => (
        <button
          onClick={() => {
            navigator.clipboard.writeText(String(row.refund_id || ""));
            toast.success("Refund ID copied!");
          }}
          className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
        >
          Copy Refund ID
        </button>
      ),
    },
  ];

  useEffect(() => {
    fetchRefundRequests(eventId, page, search);
  }, [eventId, page, search]);

  const fetchRefundRequests = async (
    eventId?: number,
    activePageNo?: number,
    searchTerm?: any
  ) => {
    try {
      setLoading(true);
      const params: any = {
        page: activePageNo,
        limit: 50,
        search: searchTerm,
        ...(eventId ? { eventId } : {}), // ✅ adds eventId only if it exists
      };

      const response: any = await apiService.get(
        "/event/ticket/get-approved-ticket-refunds",
        params
      );
      setRefunds(response?.data?.data || []); // ✅ updated: response.data is now event array
      setTotalRecords(response?.data?.totalCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  // event/ticket/get-ticket-refund-requests

  const handleSearch = (searchKey?: any) => {
    console.log("Searching for Event ID:", searchKey);
    setEventId(searchKey.trim());
    setPage(1);
  };
  return (
    <Layout
      pageTitle="Refund Approved Requests"
      eventId={queryEventId || eventId}
      searchByEventId={{
        label: "Search by Event ID",
        onClick: (val?: any) => {
          handleSearch(val);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-2 px-4">
        <DynamicTable
          title={`Refund Approved Requests`}
          data={refunds}
          columns={columns}
          pageSize={pageSize}
          loading={loading}
          activeNUmber={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalPages={totalPages}
          totalRecords={totalRecords}
          onSearch={(val?: any) => {
            setPage(1);
            setSearch(val);
          }}
        />
      </div>
    </Layout>
  );
}
